import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Modal, Spinner } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { Icon } from '@iconify/react/dist/iconify.js';
import api from '../../services/api';
import { toast } from 'react-toastify';
import ViewCollectionRequestModal from './ViewCollectionRequestModal';

const isPendingWithdrawal = (row) =>
  String(row.status || '').toLowerCase() === 'pending';

const normalizeStatusDisplay = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'paid') return 'Paid';
  /* legacy rows */
  if (s === 'approved') return 'Paid';
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const statusBadgeClass = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'pending') return 'warning';
  if (s === 'paid' || s === 'approved') return 'success';
  if (s === 'rejected' || s === 'declined') return 'danger';
  return 'secondary';
};

const CollectionRequestsTable = () => {
  const [data, setData] = useState([]);

  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [currentRequest, setCurrentRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [transactionNumber, setTransactionNumber] = useState('');
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    fetchCollectionRequests();
  }, []);

  const fetchCollectionRequests = async () => {
    setLoading(true);
    try {
      const fetchByStatus = async (status) => {
        try {
          const res = await api.get(
            `/transaction/unpaginated?inOrOut=out&status=${encodeURIComponent(status)}`
          );
          return res.data?.response?.data ?? [];
        } catch (e) {
          console.warn(`Collection requests (${status}):`, e);
          return [];
        }
      };

      const [pending, rejected, paid] = await Promise.all([
        fetchByStatus('pending'),
        fetchByStatus('rejected'),
        fetchByStatus('paid'),
      ]);

      const byId = new Map();
      [...pending, ...rejected, ...paid].forEach((row) => {
        if (row && row.id != null) byId.set(row.id, row);
      });

      const merged = [...byId.values()].sort((a, b) => {
        const ta = new Date(a.updated_at || a.created_at || 0).getTime();
        const tb = new Date(b.updated_at || b.created_at || 0).getTime();
        return tb - ta;
      });

      setData(merged);
    } catch (error) {
      console.error('Error fetching collection requests:', error);
      toast.error(
        error.response?.data?.message ||
          'Failed to load collection requests'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleView = (request) => {
    setCurrentRequest(request);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setCurrentRequest(null);
  };

  const openApproveModal = (request) => {
    setCurrentRequest(request);
    setShowApproveModal(true);
  };

  const closeApproveModal = () => {
    setShowApproveModal(false);
    setTransactionNumber('');
    setCurrentRequest(null);
  };

  const confirmApprove = async () => {
    setLoading(true);
    try {
      const payload = {
        transactionNumber,
        transaction_id: currentRequest.id,
      };
      await api.post(`/wallet/approveWithdrawal`, payload);
      toast.success('Request Approved Successfully!');
      closeApproveModal();
      fetchCollectionRequests();
    } catch (error) {
      toast.error('Failed to Approve Request');
      console.error(error);
    }
    setLoading(false);
  };

  const openDeclineModal = (request) => {
    setCurrentRequest(request);
    setShowDeclineModal(true);
  };

  const closeDeclineModal = () => {
    setShowDeclineModal(false);
    setDeclineReason('');
    setCurrentRequest(null);
  };

  const confirmDecline = async () => {
    setLoading(true);
    try {
      await api.post(`/wallet/rejectWithdrawal`, {
        reason: declineReason,
        transaction_id: currentRequest.id,
      });
      toast.success('Request Declined Successfully!');
      closeDeclineModal();
      fetchCollectionRequests();
    } catch (error) {
      toast.error('Failed to Decline Request');
      console.error(error);
    }
    setLoading(false);
  };

  const columns = [
    {
      name: 'Broker Name',
      selector: (row) => row.broker?.name ?? '',
      sortable: true,
    },
    {
      name: 'Broker Number',
      selector: (row) => row.broker?.mobile ?? '',
      sortable: true,
    },
    {
      name: 'Reason',
      selector: (row) => row.type ?? '',
      sortable: true,
    },
    {
      name: 'Amount',
      selector: (row) => row.amount ?? '',
      sortable: true,
      cell: (row) => {
        const a = row.amount;
        if (a === undefined || a === null || a === '') return '—';
        const n = Number(a);
        return Number.isNaN(n) ? String(a) : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      right: true,
    },
    {
      name: 'Status',
      selector: (row) => normalizeStatusDisplay(row.status),
      sortable: true,
      cell: (row) => (
        <span
          className={`badge rounded-pill px-12 py-4 bg-${statusBadgeClass(row.status)} text-white`}
        >
          {normalizeStatusDisplay(row.status)}
        </span>
      ),
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="d-flex flex-wrap gap-1 align-items-center">
          {/* View — always */}
          <button
            type="button"
            onClick={() => handleView(row)}
            className="w-32-px h-32-px bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0 p-0"
            title="View details"
            aria-label={`View collection request`}
          >
            <Icon icon="iconamoon:eye-light" />
          </button>
          {isPendingWithdrawal(row) && (
            <>
              <button
                type="button"
                onClick={() => openApproveModal(row)}
                className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0 p-0"
                title="Approve"
                aria-label="Approve"
              >
                <Icon icon="mdi:check-circle-outline" />
              </button>
              <button
                type="button"
                onClick={() => openDeclineModal(row)}
                className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0 p-0"
                title="Decline"
                aria-label="Decline"
              >
                <Icon icon="mdi:close-circle-outline" />
              </button>
            </>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: '120px',
    },
  ];

  const csvHeaders = [
    { label: 'Broker Name', key: 'broker.name' },
    { label: 'Broker Number', key: 'broker.mobile' },
    { label: 'Reason', key: 'type' },
    { label: 'Amount', key: 'amount' },
    { label: 'Status', key: 'status' },
    { label: 'Method', key: 'method' },
    { label: 'Name On Cheque', key: 'nameOnCheque' },
    { label: 'Transaction Number', key: 'transactionNumber' },
  ];

  const filteredData = data.filter((item) => {
    const brokerName = item.broker?.name?.toLowerCase() || '';
    const brokerMobile = item.broker?.mobile?.toLowerCase() || '';
    const reason = (item.type || '').toLowerCase();
    const st = String(item.status || '').toLowerCase();
    const search = filterText.toLowerCase();
    const matchesSearch =
      brokerName.includes(search) ||
      brokerMobile.includes(search) ||
      reason.includes(search);
    if (!statusFilter) return matchesSearch;
    const sf = statusFilter.toLowerCase();
    if (sf === 'paid') {
      return matchesSearch && (st === 'paid' || st === 'approved');
    }
    return matchesSearch && st === sf;
  });

  const customStyles = {
    rows: {
      style: {
        fontSize: '15px',
      },
    },
  };

  return (
    <div className="card basic-data-table px-3 py-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <input
              type="text"
              placeholder="Search by broker name, number, or reason"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="form-control"
              style={{ width: '250px', minWidth: '200px' }}
            />
            <select
              className="form-select"
              style={{ width: '180px' }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <CSVLink
            data={filteredData}
            headers={csvHeaders}
            filename="collection-requests.csv"
            className="btn btn-secondary"
          >
            Export CSV
          </CSVLink>
        </div>

        <DataTable
          columns={columns}
          data={filteredData}
          progressPending={loading}
          pagination
          highlightOnHover
          responsive
          striped
          customStyles={customStyles}
          noDataComponent={loading ? '' : 'No results found'}
        />
      </div>

      {showViewModal && (
        <ViewCollectionRequestModal
          show={showViewModal}
          handleClose={handleCloseViewModal}
          collectionRequest={currentRequest}
        />
      )}

      <Modal show={showApproveModal} onHide={closeApproveModal}>
        <Modal.Header closeButton>
          <Modal.Title>Approve Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label>Transaction Number</label>
            <input
              type="text"
              value={transactionNumber}
              onChange={(e) => setTransactionNumber(e.target.value)}
              className="form-control"
              placeholder="Enter transaction number"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary" onClick={confirmApprove} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Confirm'}
          </button>
          <button className="btn btn-secondary" onClick={closeApproveModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeclineModal} onHide={closeDeclineModal}>
        <Modal.Header closeButton>
          <Modal.Title>Decline Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label>Reason for Declining</label>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="form-control"
              rows="3"
              placeholder="Enter a reason for declining"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-danger" onClick={confirmDecline} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Confirm Decline'}
          </button>
          <button className="btn btn-secondary" onClick={closeDeclineModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CollectionRequestsTable;
